// ----------------------------------------------------------------------------
// App — Space Coast Procurement Portal (frontend-only investor demo)
// ----------------------------------------------------------------------------
// Routing is a simple `currentView` state switch — no React Router needed for
// the demo. A future buyer can drop this view-switch in any router (Next.js
// App Router or react-router) without changing the underlying view components.
//
// Architecture layers:
//   AuthProvider                ← session gate (isAuthenticated flag)
//   └── OwnerSettingsProvider   ← SaaS owner config (LLM/Stripe/BaaS)
//       └── SavedContractsProvider ← vendor-scoped saved bids
//           └── <Shell />       ← gateway vs. dashboard branch
// ----------------------------------------------------------------------------

import React, { useState } from "react";
import "@/App.css";
import { Toaster } from "sonner";
import { Sidebar } from "@/components/Sidebar";
import { AuthGateway } from "@/components/AuthGateway";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SavedContractsProvider } from "@/context/SavedContractsContext";
import { OwnerSettingsProvider } from "@/context/OwnerSettingsContext";
import { ActiveBidsView } from "@/components/views/ActiveBidsView";
import { SavedContractsView } from "@/components/views/SavedContractsView";
import { AlertSettingsView } from "@/components/views/AlertSettingsView";
import { DeveloperApiView } from "@/components/views/DeveloperApiView";
import { OwnerSetupWizardView } from "@/components/views/OwnerSetupWizardView";

// Inner shell — picks between the auth gateway and the authenticated
// dashboard tree based on the session flag.
function Shell() {
  const { isAuthenticated } = useAuth();
  const [currentView, setCurrentView] = useState("active");

  // Bypass the entire dashboard until the user authenticates.
  if (!isAuthenticated) return <AuthGateway />;

  const renderView = () => {
    switch (currentView) {
      case "active":
        return <ActiveBidsView />;
      case "saved":
        return <SavedContractsView />;
      case "alerts":
        return <AlertSettingsView />;
      case "api":
        return <DeveloperApiView />;
      case "wizard":
        return <OwnerSetupWizardView />;
      default:
        return <ActiveBidsView />;
    }
  };

  return (
    <div className="App min-h-screen bg-[#f8f9fa] text-slate-900 flex">
      <Sidebar currentView={currentView} onSelect={setCurrentView} />
      <main className="flex-1 min-w-0">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-8 sm:py-10">
          {renderView()}
        </div>
      </main>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <OwnerSettingsProvider>
        <SavedContractsProvider>
          <Shell />
          <Toaster position="bottom-right" richColors closeButton />
        </SavedContractsProvider>
      </OwnerSettingsProvider>
    </AuthProvider>
  );
}

export default App;
